/**
 * CrossingsMap - Carte interactive des croisements (Leaflet)
 * Affiche une carte avec les positions des utilisateurs croisés
 * Inclut une Heat Map pour visualiser les zones de forte densité
 */

import React, { useState, useEffect, useRef } from 'react';

export function CrossingsMap({ center, crossings, userLocation, isDarkMode, onSelectCrossing, showHeatmap = true }) {
  const [MapContainer, setMapContainer] = useState(null);
  const [TileLayer, setTileLayer] = useState(null);
  const [Popup, setPopup] = useState(null);
  const [CircleMarker, setCircleMarker] = useState(null);
  const [useMapEvents, setUseMapEvents] = useState(null);
  const [leafletLoaded, setLeafletLoaded] = useState(false);
  const heatLayerRef = useRef(null);
  const mapRef = useRef(null);
  
  useEffect(() => {
    // Import dynamique de react-leaflet
    Promise.all([
      import('react-leaflet'),
      import('leaflet'),
      import('leaflet/dist/leaflet.css')
    ]).then(([reactLeaflet, L]) => {
      setMapContainer(() => reactLeaflet.MapContainer);
      setTileLayer(() => reactLeaflet.TileLayer);
      setPopup(() => reactLeaflet.Popup);
      setCircleMarker(() => reactLeaflet.CircleMarker);
      setUseMapEvents(() => reactLeaflet.useMapEvents);
      
      // Charger le plugin heatmap
      import('leaflet.heat').then(() => {
        setLeafletLoaded(true);
      });
    });
  }, []);
  
  // Composant interne pour gérer la heat map
  function HeatmapLayer({ points }) {
    const map = useMapEvents({});
    
    useEffect(() => {
      if (!map || !points?.length || !window.L?.heatLayer) return;
      
      // Supprimer l'ancienne couche
      if (heatLayerRef.current) {
        map.removeLayer(heatLayerRef.current);
      }
      
      // Créer les données pour la heat map
      // Format: [lat, lng, intensity]
      const heatData = points
        .filter(p => p.lat && p.lng)
        .map(p => [p.lat, p.lng, (p.count || 1) * 0.5]);
      
      if (heatData.length > 0) {
        // Créer la nouvelle couche heat map
        heatLayerRef.current = window.L.heatLayer(heatData, {
          radius: 35,
          blur: 25,
          maxZoom: 17,
          max: 1.0,
          gradient: {
            0.2: '#3b82f6',  // Bleu - peu de croisements
            0.4: '#8b5cf6',  // Violet
            0.6: '#ec4899',  // Rose
            0.8: '#f97316',  // Orange
            1.0: '#ef4444'   // Rouge - beaucoup de croisements
          }
        });
        
        heatLayerRef.current.addTo(map);
      }
      
      return () => {
        if (heatLayerRef.current && map) {
          map.removeLayer(heatLayerRef.current);
        }
      };
    }, [map, points]);
    
    return null;
  }
  
  // Composant pour sauvegarder la référence de la map
  function MapRefSetter() {
    const map = useMapEvents({
      load: () => {
        mapRef.current = map;
      }
    });
    
    useEffect(() => {
      if (map) {
        mapRef.current = map;
      }
    }, [map]);
    
    return null;
  }
  
  if (!MapContainer || !TileLayer || !CircleMarker || !leafletLoaded) {
    return (
      <div className={`h-full flex items-center justify-center ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
        <div className="animate-spin w-8 h-8 border-4 border-pink-500 border-t-transparent rounded-full" />
      </div>
    );
  }
  
  return (
    <MapContainer 
      center={[center.lat, center.lng]} 
      zoom={15} 
      style={{ height: '100%', width: '100%' }}
      scrollWheelZoom={true}
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        url={isDarkMode 
          ? "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
          : "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        }
      />
      
      {/* Composant pour sauvegarder la ref */}
      {useMapEvents && <MapRefSetter />}
      
      {/* Heat Map Layer */}
      {showHeatmap && useMapEvents && crossings.length > 0 && (
        <HeatmapLayer points={crossings} />
      )}
      
      {/* Position de l'utilisateur */}
      {userLocation && (
        <CircleMarker
          center={[userLocation.lat, userLocation.lng]}
          radius={12}
          pathOptions={{ 
            color: '#3b82f6', 
            fillColor: '#3b82f6', 
            fillOpacity: 0.8,
            weight: 3
          }}
        >
          {Popup && <Popup>📍 Vous êtes ici</Popup>}
        </CircleMarker>
      )}
      
      {/* Marqueurs des croisements */}
      {crossings.map((crossing, index) => (
        crossing.lat && crossing.lng && (
          <CircleMarker
            key={crossing.id || index}
            center={[crossing.lat, crossing.lng]}
            radius={8 + (crossing.count || 1) * 2}
            pathOptions={{ 
              color: '#ec4899', 
              fillColor: '#f97316', 
              fillOpacity: 0.7,
              weight: 2
            }}
            eventHandlers={{
              click: () => onSelectCrossing?.(crossing)
            }}
          >
            {Popup && (
              <Popup>
                <div className="text-center">
                  <div className="text-2xl">{crossing.avatar}</div>
                  <div className="font-bold">{crossing.displayName}</div>
                  <div className="text-sm text-gray-500">
                    Croisé(e) {crossing.count}x
                  </div>
                  <div className="text-xs text-gray-400">
                    📍 {crossing.locationName || 'Ici'}
                  </div>
                </div>
              </Popup>
            )}
          </CircleMarker>
        )
      ))}
    </MapContainer>
  );
}

export default CrossingsMap;
